/**
 * Gradient Builder Component
 * Visual builder for CSS linear and radial gradients
 */

import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

interface GradientStop {
  color: string;
  position: number;
}

interface GradientBuilderProps {
  value: string;
  onChange: (gradient: string) => void;
}

function parseGradient(value: string): {
  type: 'linear' | 'radial';
  angle: number;
  stops: GradientStop[];
} {
  const defaults = {
    type: 'linear' as const,
    angle: 180,
    stops: [
      { color: '#3b82f6', position: 0 },
      { color: '#8b5cf6', position: 100 },
    ],
  };

  if (!value || !value.includes('gradient')) return defaults;

  try {
    const isRadial = value.startsWith('radial');
    const type = isRadial ? 'radial' : 'linear';
    const angle = isRadial ? 0 : parseInt(value.match(/(\d+)deg/)?.[1] || '180');
    const colorMatches = value.matchAll(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))\s+(\d+)%/g);
    const stops: GradientStop[] = [];
    for (const match of colorMatches) {
      stops.push({ color: match[1], position: parseInt(match[2]) });
    }
    if (stops.length < 2) return defaults;
    return { type, angle, stops };
  } catch {
    return defaults;
  }
}

function buildGradientString(
  type: 'linear' | 'radial',
  angle: number,
  stops: GradientStop[]
): string {
  const stopsStr = stops
    .sort((a, b) => a.position - b.position)
    .map((s) => `${s.color} ${s.position}%`)
    .join(', ');

  if (type === 'radial') {
    return `radial-gradient(circle, ${stopsStr})`;
  }
  return `linear-gradient(${angle}deg, ${stopsStr})`;
}

export function GradientBuilder({ value, onChange }: GradientBuilderProps) {
  const parsed = parseGradient(value);
  const [type, setType] = useState(parsed.type);
  const [angle, setAngle] = useState(parsed.angle);
  const [stops, setStops] = useState(parsed.stops);

  const updateGradient = (
    newType: typeof type = type,
    newAngle: number = angle,
    newStops: GradientStop[] = stops
  ) => {
    onChange(buildGradientString(newType, newAngle, newStops));
  };

  const addStop = () => {
    const newStops = [...stops, { color: '#ffffff', position: 50 }];
    setStops(newStops);
    updateGradient(type, angle, newStops);
  };

  const removeStop = (index: number) => {
    if (stops.length <= 2) return;
    const newStops = stops.filter((_, i) => i !== index);
    setStops(newStops);
    updateGradient(type, angle, newStops);
  };

  const updateStop = (index: number, field: keyof GradientStop, value: string | number) => {
    const newStops = stops.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    );
    setStops(newStops);
    updateGradient(type, angle, newStops);
  };

  return (
    <div className="space-y-3">
      {/* Preview */}
      <div
        className="h-12 rounded border"
        style={{ background: buildGradientString(type, angle, stops) }}
      />

      {/* Type & Angle */}
      <div className="flex items-center gap-2">
        <select
          value={type}
          onChange={(e) => {
            const newType = e.target.value as 'linear' | 'radial';
            setType(newType);
            updateGradient(newType, angle, stops);
          }}
          className="border rounded px-2 py-1 text-xs flex-1"
        >
          <option value="linear">Linear</option>
          <option value="radial">Radial</option>
        </select>
        {type === 'linear' && (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={angle}
              onChange={(e) => {
                const newAngle = parseInt(e.target.value) || 0;
                setAngle(newAngle);
                updateGradient(type, newAngle, stops);
              }}
              className="w-16 border rounded px-2 py-1 text-xs"
              min={0}
              max={360}
            />
            <span className="text-xs text-gray-500">deg</span>
          </div>
        )}
      </div>

      {/* Color stops */}
      <div className="space-y-2">
        {stops.map((stop, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="color"
              value={stop.color}
              onChange={(e) => updateStop(index, 'color', e.target.value)}
              className="w-7 h-7 rounded border cursor-pointer flex-shrink-0"
            />
            <input
              type="number"
              value={stop.position}
              onChange={(e) => updateStop(index, 'position', parseInt(e.target.value) || 0)}
              className="w-14 border rounded px-2 py-1 text-xs"
              min={0}
              max={100}
            />
            <span className="text-xs text-gray-500">%</span>
            {stops.length > 2 && (
              <button
                type="button"
                onClick={() => removeStop(index)}
                className="p-0.5 hover:bg-red-50 rounded text-red-400"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addStop}
        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
      >
        <Plus className="h-3 w-3" />
        Add color stop
      </button>
    </div>
  );
}
